import type { Editor } from '@tiptap/react';
import { useState } from 'react';
import type { User } from '../../../types/auth';
import type { Section, Tender } from '../../../types/tender';
import { logTenderActivity } from '../../../utils/activityLogger';

export function useSectionCRUD(
  tenderId: string,
  tender: Tender,
  account: User | null,
  createSection: (title: string, level: number, index: number) => Promise<string | null>,
  deleteSection: (sectionId: string) => Promise<void>,
  updateStatus: (section: Section, status: string) => Promise<void>,
  editingSectionId: string | null,
  setEditingSectionId: (id: string | null) => void,
  setActiveEditor: (editor: Editor | null) => void,
  unlock: (section: Section) => Promise<void>,
  setExpandedSectionIds: React.Dispatch<React.SetStateAction<Set<string>>>
) {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newSectionIndex, setNewSectionIndex] = useState<number>(0);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newSectionLevel, setNewSectionLevel] = useState(2);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<Section | null>(null);

  const handleOpenAddDialog = (index: number) => {
    setNewSectionIndex(index);
    setNewSectionTitle('');
    setNewSectionLevel(2);
    setAddDialogOpen(true);
  };

  const handleConfirmAddSection = async () => {
    if (!newSectionTitle.trim()) {
      return;
    }
    const id = await createSection(newSectionTitle, newSectionLevel, newSectionIndex);
    setAddDialogOpen(false);
    if (id) {
      logTenderActivity.updated({
        id: tenderId,
        title: tender.title,
        updateType: 'section_created',
        reason: `Created section "${newSectionTitle}"`,
      });
      setTimeout(() => {
        setExpandedSectionIds((prev) => {
          const next = new Set(prev);
          next.add(id);
          return next;
        });
      }, 500);
    }
  };

  const handleDeleteClick = (section: Section) => {
    setSectionToDelete(section);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (sectionToDelete) {
      await deleteSection(sectionToDelete.id);
      logTenderActivity.updated({
        id: tenderId,
        title: tender.title,
        updateType: 'section_deleted',
        reason: `Deleted section "${sectionToDelete.title}"`,
      });
      setDeleteDialogOpen(false);
      setSectionToDelete(null);
    }
  };

  const handleStatusChange = async (section: Section, newStatus: string) => {
    await updateStatus(section, newStatus);
    if (newStatus === 'reviewed' || newStatus === 'complete') {
      if (editingSectionId === section.id) {
        setEditingSectionId(null);
        setActiveEditor(null);
      }
      if (section.lockedBy && section.lockedBy.uid === account?.uid) {
        await unlock(section);
      }
      if (account?.uid) {
        try {
          const { collection, query, where, getDocs, updateDoc, doc } = await import(
            'firebase/firestore'
          );
          const { firestore } = await import('../../../services/firebase');
          const actionsRef = collection(firestore, 'users', account.uid, 'actions');
          const q = query(
            actionsRef,
            where('status', '==', 'pending'),
            where('metadata.type', '==', 'assignment_action'),
            where('metadata.sectionId', '==', section.id)
          );
          const snapshot = await getDocs(q);
          for (const actionDoc of snapshot.docs) {
            await updateDoc(doc(firestore, 'users', account.uid, 'actions', actionDoc.id), {
              status: 'completed',
            });
          }
        } catch (_e) {
          // ignore
        }
      }
    }
  };

  return {
    addDialogOpen,
    setAddDialogOpen,
    newSectionIndex,
    newSectionTitle,
    setNewSectionTitle,
    newSectionLevel,
    setNewSectionLevel,
    handleOpenAddDialog,
    handleConfirmAddSection,
    deleteDialogOpen,
    setDeleteDialogOpen,
    sectionToDelete,
    handleDeleteClick,
    handleConfirmDelete,
    handleStatusChange,
  };
}
